/**
 * Custom Packet Builder Component
 */

import React from 'react';
import { useDiaperStore } from '../../stores/diaperStore';
import { useSimulatorStore } from '../../stores/simulatorStore';
import { useToast } from '../../stores/toastStore';
import { WetnessCurveEnhanced } from './WetnessCurveEnhanced';
import { WetnessSlider } from './WetnessSlider';
import { PayloadBreakdown } from './PayloadBreakdown';
import { calculateWetnessVolume } from '../../lib/utils';

export const CustomPacketBuilder: React.FC = () => {
  const { diaperModels } = useDiaperStore();
  const {
    selectedClip,
    selectClip,
    wetnessLevel,
    statusCode,
    battery,
    diaperCounter,
    changeNeededPercent,
    currentCriterion,
    setWetnessLevel,
    setStatusCode,
    setBattery,
    setDiaperCounter,
    sendPacket,
    isSendingPacket,
  } = useSimulatorStore();
  const toast = useToast();

  const diaperModelCode = selectedClip?.diaperModelCode || diaperModels[0]?.code || 30;
  const diaperModel = diaperModels.find((m) => m.code === diaperModelCode) || null;
  const wetnessCalc = calculateWetnessVolume(wetnessLevel, diaperModel);

  const handleSend = async () => {
    if (!selectedClip) {
      toast.error('Please select a session first');
      return;
    }

    try {
      await sendPacket(`Custom: wetness ${wetnessLevel}, status ${statusCode}, battery ${battery}%`);
      toast.success('Custom packet sent successfully');
    } catch (error: any) {
      toast.error(`Failed to send packet: ${error.message}`);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mt-6">
      <h3 className="text-xl font-bold mb-4">Custom Packet (13-byte BLE Payload)</h3>
      
      <p className="text-sm text-gray-600 mb-4">
        📡 Build a custom sensor packet following the BLE Advertisement format. Bytes 2-3 (wetness) and
        4-5 (model) use little-endian encoding.
      </p>

      <div className="space-y-6">
        {/* Enhanced Wetness Slider with Presets */}
        <div>
          <WetnessSlider
            value={wetnessLevel}
            onChange={setWetnessLevel}
            diaperModel={diaperModel}
            changeNeededPercent={changeNeededPercent}
          />

          {wetnessCalc.model && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
              🧮 Backend will calculate: <strong>{wetnessCalc.volume} mL</strong> ({wetnessCalc.percent}
              % of {wetnessCalc.model.capacity}mL capacity)
            </div>
          )}

          {/* Warning for invalid wetness decrease */}
          {selectedClip && wetnessLevel < (selectedClip.wetnessLevel || 0) && (
            <div className="mt-3 p-3 bg-orange-50 border-l-4 border-orange-500 rounded">
              <div className="flex items-start gap-2">
                <span className="text-orange-600 text-lg">⚠️</span>
                <div className="text-sm">
                  <strong className="text-orange-800">Warning: Decreasing wetness</strong>
                  <p className="text-orange-700 mt-1">
                    You're setting wetness to <strong>{wetnessLevel}</strong>, which is lower than the 
                    backend-reported value of <strong>{selectedClip.wetnessLevel}</strong> (
                    {selectedClip.rawSession?.wetnessPercent?.toFixed(1) || '—'}%). 
                    A real clip would never send a lower wetness value than previously reported.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Wetness Curve */}
          <div className="mt-4">
            <WetnessCurveEnhanced
              diaperModel={diaperModel}
              currentWetness={wetnessLevel}
              reportedWetness={selectedClip?.wetnessLevel}
              reportedWetnessPercent={selectedClip?.rawSession?.wetnessPercent}
              reportedWetnessVolume={selectedClip?.rawSession?.wetnessVolume}
              changeNeededPercent={changeNeededPercent}
              stabilizationPeriod={currentCriterion?.content.stabilizationPeriod_sec}
              onWetnessChange={setWetnessLevel}
            />
          </div>
        </div>

        {/* Form Controls Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Status Code */}
          <div>
            <label className="block font-semibold mb-2">Status Code (Byte 1) • Signed byte</label>
            <select
              value={statusCode}
              onChange={(e) => setStatusCode(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="0">0 - Attached (OK)</option>
              <option value="1">1 - Detached</option>
              <option value="2">2 - Shorted</option>
              <option value="3">3 - Same Wet Diaper Attached</option>
              <option value="4">4 - Wet Diaper Attached</option>
              <option value="5">5 - Leak Imminent</option>
              <option value="6">6 - Identifying</option>
              <option value="7">7 - Preattached (Clip Rebooted)</option>
              <option value="8">8 - Recovered</option>
              <option value="9">9 - Entering Sleep Mode</option>
              <option value="30">30 - Pooling</option>
              <option value="255">255 - Boot (0xFF)</option>
            </select>
          </div>

          {/* Battery Level */}
          <div>
            <label className="block font-semibold mb-2">Battery Level (Byte 12) • 0-100%</label>
            <input
              type="number"
              min="0"
              max="100"
              value={battery}
              onChange={(e) => setBattery(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Diaper Counter */}
          <div>
            <label className="block font-semibold mb-2">Diaper Counter (Byte 6) • 0-255</label>
            <p className="text-xs text-gray-600 mb-2">
              Increments each diaper change. Backend detects changes and closes current diaper.
            </p>
            <input
              type="number"
              min="0"
              max="255"
              value={diaperCounter}
              onChange={(e) => setDiaperCounter(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Diaper Model */}
          <div>
            <label className="block font-semibold mb-2">Diaper Model (Bytes 4-5) • 16-bit little-endian</label>
            <select
              value={diaperModelCode}
              onChange={(e) => {
                const newModelCode = Number(e.target.value);
                // Update the selected clip's diaper model
                if (selectedClip) {
                  selectClip({
                    ...selectedClip,
                    diaperModelCode: newModelCode,
                  });
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {diaperModels
                .sort((a, b) => a.code - b.code)
                .map((model) => (
                  <option key={model.code} value={model.code}>
                    {model.name} ({Math.round(model.capacity)}ml)
                  </option>
                ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">💡 Change the diaper model to update capacity calculations and leak thresholds</p>
          </div>
        </div>

        {/* Payload Breakdown Table */}
        <PayloadBreakdown />

        {/* Send Button */}
        <button
          onClick={handleSend}
          disabled={!selectedClip || isSendingPacket}
          className="w-full py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-light disabled:opacity-60 disabled:cursor-not-allowed transition-all"
        >
          {isSendingPacket ? '⏳ Sending...' : '📤 Send Custom Packet'}
        </button>
      </div>
    </div>
  );
};

